'use client'

/**
 * SuccessIndicator Component
 *
 * Visual gauge showing how the user's calculated value compares
 * to the ideal range. Helps first-time users understand if their
 * results are good, too high, or too low.
 *
 * Usage:
 *   <SuccessIndicator
 *     value={1.2}
 *     idealMin={1.0}
 *     idealMax={1.5}
 *     unit="inches"
 *     label="Weekly Water"
 *   />
 */

import * as React from 'react'
import { cn } from '@/lib/utils'
import { SuccessIndicatorConfig } from './types'
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'

interface SuccessIndicatorProps extends SuccessIndicatorConfig {
  /** Additional CSS classes */
  className?: string
  /** Show descriptive text below gauge */
  showDescription?: boolean
  /** Compact mode - no gauge, just status */
  compact?: boolean
}

type ValueStatus = 'below' | 'ideal' | 'above'

/**
 * Determines where the value falls relative to the ideal range
 */
function getValueStatus(value: number, min: number, max: number): ValueStatus {
  if (value < min) return 'below'
  if (value > max) return 'above'
  return 'ideal'
}

/**
 * Calculates the percentage position on the gauge
 * Maps the range to a 0-100 scale with the ideal range in the middle
 */
function getGaugePosition(value: number, min: number, max: number): number {
  const range = max - min
  const buffer = range * 0.5 // Allow 50% buffer on each side

  const adjustedMin = min - buffer
  const adjustedMax = max + buffer
  const totalRange = adjustedMax - adjustedMin

  const position = ((value - adjustedMin) / totalRange) * 100
  return Math.max(0, Math.min(100, position))
}

/**
 * Generates a description of the value's position
 */
function getDescription(
  value: number,
  min: number,
  max: number,
  unit: string,
  label: string
): string {
  const status = getValueStatus(value, min, max)
  const unitStr = unit ? ` ${unit}` : ''

  switch (status) {
    case 'below':
      return `Your ${value}${unitStr} is below the ideal range (${min}-${max}${unitStr})`
    case 'ideal':
      return `Your ${value}${unitStr} is within the ideal range (${min}-${max}${unitStr})`
    case 'above':
      return `Your ${value}${unitStr} is above the ideal range (${min}-${max}${unitStr})`
  }
}

/**
 * Visual gauge showing value vs ideal range with status indicator.
 */
export function SuccessIndicator({
  value,
  idealMin,
  idealMax,
  unit = '',
  label,
  showGauge = true,
  showDescription = true,
  compact = false,
  className,
}: SuccessIndicatorProps) {
  const status = getValueStatus(value, idealMin, idealMax)
  const position = getGaugePosition(value, idealMin, idealMax)

  const statusConfig = {
    below: {
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      markerBg: 'bg-amber-500',
    },
    ideal: {
      icon: CheckCircle2,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      markerBg: 'bg-green-500',
    },
    above: {
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      markerBg: 'bg-amber-500',
    },
  }

  const config = statusConfig[status]
  const Icon = config.icon

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Icon className={cn('h-4 w-4', config.color)} />
        <span className={cn('text-sm font-medium', config.color)}>
          {value}{unit && ` ${unit}`}
        </span>
        <span className="text-xs text-gray-500">
          (ideal: {idealMin}-{idealMax}{unit && ` ${unit}`})
        </span>
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Label and status header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-1.5">
          <Icon className={cn('h-4 w-4', config.color)} aria-hidden="true" />
          <span className={cn('text-sm font-medium', config.color)}>
            {value}{unit && ` ${unit}`}
          </span>
        </div>
      </div>

      {/* Visual gauge */}
      {showGauge && (
        <div className="relative">
          {/* Gauge track */}
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
            {/* Below zone */}
            <div className="w-1/4 bg-gray-200" />
            {/* Ideal zone */}
            <div className="w-2/4 bg-green-100" />
            {/* Above zone */}
            <div className="w-1/4 bg-gray-200" />
          </div>

          {/* Ideal range labels */}
          <div
            className="absolute top-0 h-3 border-l-2 border-green-500 opacity-50"
            style={{ left: '25%' }}
          />
          <div
            className="absolute top-0 h-3 border-r-2 border-green-500 opacity-50"
            style={{ left: '75%' }}
          />

          {/* Value marker */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-300"
            style={{ left: `${position}%` }}
          >
            <div
              className={cn(
                'h-5 w-5 rounded-full border-2 border-white shadow-md',
                config.markerBg
              )}
            />
          </div>

          {/* Range labels */}
          <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
            <span>Low</span>
            <span className="text-green-600 font-medium">
              {idealMin}-{idealMax}{unit && ` ${unit}`}
            </span>
            <span>High</span>
          </div>
        </div>
      )}

      {/* Description text */}
      {showDescription && (
        <p className={cn(
          'text-sm rounded-lg px-3 py-2',
          config.bg,
          config.border,
          'border'
        )}>
          {getDescription(value, idealMin, idealMax, unit, label)}
        </p>
      )}
    </div>
  )
}

/**
 * Simple pass/fail indicator without gauge
 */
export function StatusCheck({
  label,
  passed,
  message,
  className,
}: {
  label: string
  passed: boolean
  message?: string
  className?: string
}) {
  return (
    <div className={cn('flex items-start gap-3', className)}>
      {passed ? (
        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
      ) : (
        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
      )}
      <div>
        <span className="font-medium text-gray-800">{label}</span>
        {message && (
          <p className="text-sm text-gray-600 mt-0.5">{message}</p>
        )}
      </div>
    </div>
  )
}

/**
 * Multi-value comparison showing several metrics at once
 */
interface MultiValueComparisonProps {
  items: Array<{
    label: string
    value: number
    idealMin: number
    idealMax: number
    unit?: string
  }>
  className?: string
}

export function MultiValueComparison({ items, className }: MultiValueComparisonProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {items.map((item, index) => (
        <SuccessIndicator
          key={index}
          label={item.label}
          value={item.value}
          idealMin={item.idealMin}
          idealMax={item.idealMax}
          unit={item.unit}
          showGauge={false}
          showDescription={false}
          compact
        />
      ))}
    </div>
  )
}

export default SuccessIndicator
